import * as yup from '@if/core/yup'
import {
  ControlledChipsField,
  FormBody,
  FormContainerProvider,
  FormSubmitButton,
  useConfirmDialog,
  useSnackbar,
} from '@if/mui'
import { useImportLoansByIds } from '@src/api/admin-commands-api'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CommandCard from './CommandCard'

const LOAN_ID = /^\d+$/

const buildSchema = (invalidIdMessage: string) =>
  yup.object({
    loanIds: yup.array().of(yup.text().required().matches(LOAN_ID, invalidIdMessage)).required().min(1).default([]),
  })

type ImportLoansSchema = ReturnType<typeof buildSchema>
type ImportLoansDraft = yup.InferDraft<ImportLoansSchema>
type ImportLoansValues = yup.InferSubmit<ImportLoansSchema>
type ImportLoansOutcome = Promise<void | false>

/**
 * Import a list of monolith loan ids. The schema rejects a non-numeric token, so the
 * command can only be confirmed once every id is one — and a failure is reported the way a
 * success is, in a single snackbar, rather than as an inline form error.
 */
const ImportLoansByIdsCard = () => {
  const { t } = useTranslation()
  const { showError } = useSnackbar()
  const importLoansByIds = useImportLoansByIds()

  const schema = useMemo(() => buildSchema(t('admin.devTools.commands.importLoansByIds.invalidId')), [t])

  const confirm = useConfirmDialog<{ count: number }>({
    title: t('admin.devTools.dialog.title'),
    message: (ctx) => t('admin.devTools.commands.importLoansByIds.confirmationMessage', { nb: ctx.props.count }),
    confirmLabel: t('admin.devTools.commands.importLoansByIds.actionLabel'),
  })

  const handleSubmit = async (values: ImportLoansValues): ImportLoansOutcome => {
    if (!(await confirm.open({ count: values.loanIds.length }))) return false
    try {
      await importLoansByIds.mutateAsync(values.loanIds)
    } catch {
      showError(t('admin.devTools.messages.error'))
      return false
    }
  }

  return (
    <FormContainerProvider<ImportLoansDraft, unknown, ImportLoansOutcome, ImportLoansValues>
      mode="create"
      useFormSetup={() => ({ schema })}
      onSubmit={handleSubmit}
      successMessage={t('admin.devTools.messages.success')}
    >
      <CommandCard
        title={t('admin.devTools.commands.importLoansByIds.title')}
        description={t('admin.devTools.commands.importLoansByIds.description')}
        commandType={DevToolsCommandType.ImportLoansByIds}
        actions={<FormSubmitButton label={t('admin.devTools.commands.importLoansByIds.actionLabel')} />}
      >
        <FormBody<ImportLoansDraft, unknown, ImportLoansValues>>
          {({ control, trigger }) => (
            <ControlledChipsField
              control={control}
              trigger={trigger}
              name="loanIds"
              label={t('admin.devTools.commands.importLoansByIds.inputLabel')}
              placeholder={t('admin.devTools.commands.importLoansByIds.placeholder')}
              helperText={t('admin.devTools.commands.importLoansByIds.inputHelper')}
              required
              multiline
              minRows={3}
              maxRows={6}
            />
          )}
        </FormBody>
      </CommandCard>
    </FormContainerProvider>
  )
}

export default ImportLoansByIdsCard
